import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { StoreHeader } from '../components/StoreHeader';
import { StoreFooter } from '../components/StoreFooter';
import { ProductCard } from '../components/ProductCard';
import { productApi, categoryApi, flashSaleApi } from '../lib/api';

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [flashSales, setFlashSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterOpen, setFilterOpen] = useState(false);

  const categoryFilter = searchParams.get('category') || '';
  const searchFilter = searchParams.get('search') || '';
  const featuredFilter = searchParams.get('featured') === 'true';

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params = {};
        if (categoryFilter) params.category_id = categoryFilter;
        if (searchFilter) params.search = searchFilter;
        if (featuredFilter) params.featured = true;

        const [productsRes, categoriesRes, flashSalesRes] = await Promise.all([
          productApi.getAll(params),
          categoryApi.getAll(),
          flashSaleApi.getAll(true),
        ]);

        setProducts(productsRes.data);
        setCategories(categoriesRes.data);
        setFlashSales(flashSalesRes.data);
      } catch (error) {
        console.error('Error loading products:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [categoryFilter, searchFilter, featuredFilter]);

  const handleCategoryChange = (catId) => {
    const newParams = new URLSearchParams(searchParams);
    if (catId === categoryFilter) {
      newParams.delete('category');
    } else {
      newParams.set('category', catId);
    }
    setSearchParams(newParams);
  };

  const handleSearchChange = (value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set('search', value);
    } else {
      newParams.delete('search');
    }
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    setSearchParams({});
  };

  const FilterSidebar = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold text-stone-900 mb-4">Categories</h3>
        <div className="space-y-3">
          {categories.map((cat) => (
            <label
              key={cat.id}
              className="flex items-center gap-3 cursor-pointer"
              data-testid={`filter-category-${cat.id}`}
            >
              <Checkbox
                checked={categoryFilter === cat.id}
                onCheckedChange={() => handleCategoryChange(cat.id)}
              />
              <span className="text-sm">{cat.name}</span>
              <span className="text-xs text-stone-400 ml-auto">({cat.product_count})</span>
            </label>
          ))}
        </div>
      </div>

      {(categoryFilter || searchFilter || featuredFilter) && (
        <Button
          variant="outline"
          onClick={clearFilters}
          className="w-full"
          data-testid="clear-filters-btn"
        >
          <X className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100">
      <StoreHeader />

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-emerald-900">Home</Link>
          <span className="mx-2">/</span>
          <span className="text-stone-900">Products</span>
          {categoryFilter && categories.find(c => c.id === categoryFilter) && (
            <>
              <span className="mx-2">/</span>
              <span className="text-stone-900">
                {categories.find(c => c.id === categoryFilter)?.name}
              </span>
            </>
          )}
        </nav>

        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-stone-900">
              {searchFilter
                ? `Search: "${searchFilter}"`
                : categoryFilter
                  ? categories.find(c => c.id === categoryFilter)?.name || 'Products'
                  : featuredFilter
                    ? 'Featured Products'
                    : 'All Products'}
            </h1>
            <p className="text-stone-500 mt-1">{products.length} products found</p>
          </div>

          <div className="flex items-center gap-4">
            {/* Search Input */}
            <Input
              type="text"
              placeholder="Search products..."
              value={searchFilter}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full md:w-64"
            />

            {/* Mobile Filter Button */}
            <Sheet open={filterOpen} onOpenChange={setFilterOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="md:hidden"
                  data-testid="mobile-filter-btn"
                >
                  <SlidersHorizontal className="h-4 w-4 mr-2" />
                  Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80">
                <SheetHeader>
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="mt-6">
                  <FilterSidebar />
                </div>
              </SheetContent>
            </Sheet>

            {/* Sort */}
            <Select defaultValue="newest">
              <SelectTrigger className="w-40" data-testid="sort-select">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="popular">Most Popular</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <div className="bg-white border border-stone-200 p-6 sticky top-24">
              <FilterSidebar />
            </div>
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="bg-white border border-stone-200 aspect-square skeleton"
                  ></div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-16 bg-white border border-stone-200">
                <p className="text-stone-500 mb-4">No products found</p>
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              </div>
            ) : (
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
                data-testid="products-grid"
              >
                {products.map((product) => {
                  const flashSale = flashSales.find((f) => f.product_id === product.id);

                  // Switch based on category
                  if (product.category_name === "Cakes") {
                    return (
                      <Link key={product.id} to={`/products/${product.id}?customize=true`}>
                        <ProductCard product={product} flashSale={flashSale} />
                      </Link>
                    );
                  }

                  return (
                    <Link key={product.id} to={`/products/${product.id}`}>
                      <ProductCard product={product} flashSale={flashSale} />
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      <StoreFooter />
    </div>
  );
}
